import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, Input, InputNumber, Select, Steps, Switch } from 'antd'
import { useSnackbar } from 'notistack'

import Receive from '../../components/reusable/receive'
import { notifOps, usePet } from '../../modules'

const { Step } = Steps
const { TextArea } = Input

const buildTimeZoneOptions = country => {
    if (country === 'Kenya') return ['Africa/Nairobi']
    if (country === 'Uganda') return ['Africa/Kampala']
    if (country === 'Tanzania') return ['Africa/Dar_es_Salaam']
    if (country === 'Rwanda') return ['Africa/Kigali']
    return ['Africa/Nairobi', 'Africa/Kampala', 'Africa/Dar_es_Salaam', 'Africa/Kigali', 'UTC']
}

const normalizeDraftToForm = draft => {
    if (!draft) return null

    return {
        country: draft.country || '',
        hospitalName: draft.hospitalName || '',
        abbreviation: draft.abbreviation || '',
        branchName: draft.branchName || 'Main Branch',
        address: draft.address || '',
        branchPhone: draft.branchPhone || '',
        branchEmail: draft.branchEmail || '',
        currency: draft.currency || '',
        fiscalYearMonth: draft.fiscalYearMonth ?? 7,
        fiscalYearDay: draft.fiscalYearDay ?? 1,
        adminName: draft.adminName || '',
        adminEmail: draft.adminEmail || '',
        adminPhone: draft.adminPhone || '',
        adminPassword: '',
        adminPasswordConfirm: '',
        selectedSpecialties: Array.isArray(draft.selectedSpecialties) && draft.selectedSpecialties.length > 0 ? draft.selectedSpecialties : ['General'],
        inpatientEnabled: Boolean(draft.inpatientEnabled),
        wardName: draft.wardName || 'General Ward',
        wardBeds: draft.wardBeds ?? 10,
        wardDailyRate: draft.wardDailyRate ?? 5000,
        chargemasterTemplate: draft.chargemasterTemplate || '',
        selectedSchemes: Array.isArray(draft.selectedSchemes) ? draft.selectedSchemes : [],
        customSchemes: draft.customSchemes || '',
        timeZone: draft.timeZone || '',
        initialStores: draft.initialStores || 'Main Store, Pharmacy',
        notificationEmail: draft.notificationEmail || '',
        notificationPhone: draft.notificationPhone || '',
        shaEnabled: Boolean(draft.shaEnabled),
        shaBiometricEnabled: Boolean(draft.shaBiometricEnabled),
        shaBaseUrl: draft.shaBaseUrl || '',
        shaClientId: draft.shaClientId || '',
        shaClientSecret: '',
        shaFacilityFrCode: draft.shaFacilityFrCode || '',
        shaEkycProviderId: draft.shaEkycProviderId || '',
        kephLevel: draft.kephLevel || ''
    }
}

const Setup = ({ onComplete }) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()

    const [current, setCurrent] = useState(0)
    const [countries, setCountries] = useState([])
    const [specialties, setSpecialties] = useState([])
    const [templates, setTemplates] = useState([])
    const [schemes, setSchemes] = useState([])
    const [submitPayload, setSubmitPayload] = useState(null)
    const [submitToken, setSubmitToken] = useState(0)
    const [draftPayload, setDraftPayload] = useState(null)
    const [draftToken, setDraftToken] = useState(0)
    const [restoreComplete, setRestoreComplete] = useState(false)
    const currentCountryRef = useRef('')

    const [form, setForm] = useState({
        country: '',
        hospitalName: '',
        abbreviation: '',
        branchName: 'Main Branch',
        address: '',
        branchPhone: '',
        branchEmail: '',
        currency: '',
        fiscalYearMonth: 7,
        fiscalYearDay: 1,
        adminName: '',
        adminEmail: '',
        adminPhone: '',
        adminPassword: '',
        adminPasswordConfirm: '',
        selectedSpecialties: ['General'],
        inpatientEnabled: false,
        wardName: 'General Ward',
        wardBeds: 10,
        wardDailyRate: 5000,
        chargemasterTemplate: '',
        selectedSchemes: [],
        customSchemes: '',
        timeZone: '',
        initialStores: 'Main Store, Pharmacy',
        notificationEmail: '',
        notificationPhone: '',
        shaEnabled: false,
        shaBiometricEnabled: false,
        shaBaseUrl: '',
        shaClientId: '',
        shaClientSecret: '',
        shaFacilityFrCode: '',
        shaEkycProviderId: '',
        kephLevel: ''
    })

    const [{ loading: countriesLoading, error: countriesError }] = usePet('setupReferenceData', { type: 'countries' }, {
        bypassLock: true,
        notify: true,
        callback: ({ data }) => setCountries(data || []),
        logOutOnError: false
    })

    const [{ loading: draftLoading, error: draftError }] = usePet('setupStatus', {}, {
        bypassLock: true,
        notify: false,
        callback: ({ data }) => {
            if (data?.draft) {
                const restored = normalizeDraftToForm(data.draft)
                if (restored) setForm(prev => ({ ...prev, ...restored }))
            }

            if (typeof data?.currentStep === 'number')
                setCurrent(Math.max(0, Math.min(5, data.currentStep)))

            setRestoreComplete(true)
        },
        logOutOnError: false
    })

    const [{ loading: specialtiesLoading, error: specialtiesError }] = usePet('setupReferenceData', { type: 'specialties' }, {
        bypassLock: true,
        notify: true,
        callback: ({ data }) => setSpecialties(data || []),
        logOutOnError: false
    })

    const [{ loading: templatesLoading, error: templatesError }, loadTemplates] = usePet('setupChargemasterTemplates', {
        country: form.country
    }, {
        bypassLock: true,
        notify: true,
        manual: true,
        callback: ({ data }) => {
            if (currentCountryRef.current === form.country)
                setTemplates(data || [])
        },
        logOutOnError: false
    })

    const [{ loading: schemesLoading, error: schemesError }, loadSchemes] = usePet('setupReferenceData', {
        type: 'schemes',
        country: form.country
    }, {
        bypassLock: true,
        notify: true,
        manual: true,
        callback: ({ data }) => {
            if (currentCountryRef.current === form.country)
                setSchemes(data || [])
        },
        logOutOnError: false
    })

    const [{ loading: submitLoading, error: submitError }, submitSetup] = usePet('setupInitialize', submitPayload || {}, {
        bypassLock: true,
        notify: true,
        manual: true,
        load: { msg: 'Applying setup...' },
        callback: ({ data }, notify, ops) => {
            closeSnackbar()
            notify('Setup completed successfully', ops('success'))
            // Don't navigate here on a fixed timer - App.js's own setupState.completed
            // effect redirects to /login once loadSetupStatus() (triggered by onComplete)
            // actually confirms completion, avoiding a race with that async call.
            onComplete?.()
        },
        logOutOnError: false
    })

    const [, saveDraft] = usePet('setupSaveStep', draftPayload || {}, {
        bypassLock: true,
        notify: false,
        manual: true,
        logOutOnError: false
    })

    useEffect(() => {
        currentCountryRef.current = form.country
    }, [form.country])

    useEffect(() => {
        if (!submitPayload || submitToken < 1) return
        submitSetup()
        // usePet returns a fresh execute function each render; keying this effect to submitToken
        // keeps setup submission one-shot per explicit user action instead of re-firing on rerenders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitToken])

    useEffect(() => {
        if (!draftPayload || draftToken < 1) return
        saveDraft()
        // same one-shot pattern as submit: save explicit user progress without re-firing on rerenders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftToken])

    useEffect(() => {
        if (!form.country) return
        loadTemplates()
        loadSchemes()
        // usePet returns fresh execute functions each render; this should only run when country changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.country])

    useEffect(() => {
        if (!form.country) return

        const selectedCountry = countries.find(entry => entry.code === form.country)

        setForm(prev => ({
            ...prev,
            currency: prev.currency || selectedCountry?.currency || '',
            timeZone: prev.timeZone || selectedCountry?.defaultTimeZone || ''
        }))
    }, [form.country, countries])

    useEffect(() => {
        if (templates.length > 0 && !templates.some(template => template.id === form.chargemasterTemplate)) {
            setForm(prev => ({ ...prev, chargemasterTemplate: templates[0]?.id || '' }))
        }
    }, [templates, form.chargemasterTemplate])

    const errors = useMemo(() => {
        const issues = []

        if (!form.country) issues.push('Select a country')
        if (!form.hospitalName.trim()) issues.push('Enter the hospital name')
        if (!form.adminName.trim()) issues.push('Enter the super admin name')
        if (!form.adminEmail.trim()) issues.push('Enter the super admin email')
        if (form.adminPassword.length < 12) issues.push('Use a super admin password with at least 12 characters')
        if (form.adminPassword !== form.adminPasswordConfirm) issues.push('Super admin passwords do not match')
        if (!form.chargemasterTemplate) issues.push('Select a chargemaster template')
        if (form.selectedSpecialties.length < 1) issues.push('Select at least one specialty')
        if (form.selectedSchemes.length < 1 && !form.customSchemes.trim()) issues.push('Select at least one scheme or enter a custom scheme')
        if (form.inpatientEnabled && (!form.wardName.trim() || Number(form.wardBeds) < 1)) issues.push('Provide a valid initial ward setup')
        if (form.shaEnabled && (!form.shaBaseUrl.trim() || !form.shaClientId.trim() || !form.shaClientSecret.trim()))
            issues.push('SHA base URL, client ID, and client secret are required when SHA is enabled')
        if (form.shaBiometricEnabled && !form.shaFacilityFrCode.trim())
            issues.push('SHA facility FR code is required when biometric/eKYC is enabled')
        if (form.shaEnabled && !form.kephLevel)
            issues.push('Facility KEPH level is required when SHA is enabled')

        return issues
    }, [form])

    const stepTitles = [
        'Identity',
        'Administration',
        'Financials',
        'Clinical',
        'Insurance & SHA',
        'Review'
    ]

    const goLiveChecklist = [
        'Register a new patient and confirm their chart opens correctly.',
        'Bill at least one starter service and confirm it appears on the patient bill.',
        'Route/order that service to the intended department queue.',
        'Record a payment using the Cash payment method.',
        'Complete the downstream action for that billed item: result entry, dispense, or procedure note.',
    ]

    const toggleSpecialty = specialty => {
        setForm(prev => ({
            ...prev,
            selectedSpecialties: prev.selectedSpecialties.includes(specialty)
                ? prev.selectedSpecialties.filter(entry => entry !== specialty)
                : [...prev.selectedSpecialties, specialty]
        }))
    }

    const toggleScheme = schemeId => {
        setForm(prev => ({
            ...prev,
            selectedSchemes: prev.selectedSchemes.includes(schemeId)
                ? prev.selectedSchemes.filter(entry => entry !== schemeId)
                : [...prev.selectedSchemes, schemeId]
        }))
    }

    const persistDraft = nextStep => {
        const draft = {
            country: form.country,
            hospitalName: form.hospitalName,
            abbreviation: form.abbreviation,
            branchName: form.branchName,
            address: form.address,
            branchPhone: form.branchPhone,
            branchEmail: form.branchEmail,
            currency: form.currency,
            fiscalYearMonth: form.fiscalYearMonth,
            fiscalYearDay: form.fiscalYearDay,
            adminName: form.adminName,
            adminEmail: form.adminEmail,
            adminPhone: form.adminPhone,
            selectedSpecialties: form.selectedSpecialties,
            inpatientEnabled: form.inpatientEnabled,
            wardName: form.wardName,
            wardBeds: form.wardBeds,
            wardDailyRate: form.wardDailyRate,
            chargemasterTemplate: form.chargemasterTemplate,
            selectedSchemes: form.selectedSchemes,
            customSchemes: form.customSchemes,
            timeZone: form.timeZone,
            initialStores: form.initialStores,
            notificationEmail: form.notificationEmail,
            notificationPhone: form.notificationPhone,
            shaEnabled: form.shaEnabled,
            shaBiometricEnabled: form.shaBiometricEnabled,
            shaBaseUrl: form.shaBaseUrl,
            shaClientId: form.shaClientId,
            shaFacilityFrCode: form.shaFacilityFrCode,
            shaEkycProviderId: form.shaEkycProviderId,
            kephLevel: form.kephLevel
        }

        setDraftPayload({ draft, currentStep: nextStep })
        setDraftToken(prev => prev + 1)
    }

    const performSubmit = () => {
        if (errors.length > 0) {
            enqueueSnackbar(errors[0], notifOps('warning'))
            return
        }

        const customSchemes = form.customSchemes
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
            .map(name => ({ type: 'custom', name, details: {} }))

        const payload = {
            profileVersion: 1,
            identity: {
                country: form.country,
                hospitalName: form.hospitalName.trim(),
                abbreviation: form.abbreviation.trim() || undefined,
                branch: {
                    name: form.branchName.trim() || 'Main Branch',
                    address: form.address.trim() || undefined,
                    phone: form.branchPhone.trim() || undefined,
                    email: form.branchEmail.trim() || undefined
                },
                multiBranchFromStart: false
            },
            financial: {
                currency: form.currency,
                fiscalYearStart: {
                    month: Number(form.fiscalYearMonth),
                    day: Number(form.fiscalYearDay)
                }
            },
            clinical: {
                specialties: form.selectedSpecialties,
                inpatientEnabled: form.inpatientEnabled,
                initialWards: form.inpatientEnabled ? [{
                    name: form.wardName.trim(),
                    beds: Number(form.wardBeds),
                    dailyRate: Number(form.wardDailyRate)
                }] : [],
                chargemasterTemplate: form.chargemasterTemplate
            },
            insurance: {
                schemes: [
                    ...form.selectedSchemes.map(ref => ({ type: 'scheme-catalog', ref })),
                    ...customSchemes
                ],
                sha: {
                    enabled: form.country === 'Kenya' && form.shaEnabled,
                    biometricEnabled: form.country === 'Kenya' && form.shaEnabled && form.shaBiometricEnabled,
                    baseUrl: form.shaBaseUrl.trim() || undefined,
                    clientId: form.shaClientId.trim() || undefined,
                    clientSecret: form.shaClientSecret.trim() || undefined,
                    facilityFrCode: form.shaFacilityFrCode.trim() || undefined,
                    ekycProviderId: form.shaEkycProviderId.trim() || undefined,
                    kephLevel: form.kephLevel ? Number(form.kephLevel) : undefined
                }
            },
            operations: {
                timeZone: form.timeZone,
                initialStores: form.initialStores.split(',').map(item => item.trim()).filter(Boolean),
                notificationContact: {
                    email: form.notificationEmail.trim() || undefined,
                    phone: form.notificationPhone.trim() || undefined
                }
            },
            superadmin: {
                name: form.adminName.trim(),
                email: form.adminEmail.trim(),
                phone: form.adminPhone.trim() || undefined,
                password: form.adminPassword
            }
        }

        setSubmitPayload(payload)
        setSubmitToken(prev => prev + 1)
    }

    const renderStep = () => {
        if (current === 0) {
            return (
                <div className="grid gap-4 md:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium mb-1">Country</label>
                        <Select className="w-full" value={ form.country || undefined } onChange={ value => setForm(prev => ({ ...prev, country: value })) }>
                            {countries.map(country => <Select.Option key={ country.code } value={ country.code }>{ country.name }</Select.Option>)}
                        </Select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Hospital name</label>
                        <Input value={ form.hospitalName } onChange={ e => setForm(prev => ({ ...prev, hospitalName: e.target.value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Abbreviation</label>
                        <Input value={ form.abbreviation } onChange={ e => setForm(prev => ({ ...prev, abbreviation: e.target.value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Branch name</label>
                        <Input value={ form.branchName } onChange={ e => setForm(prev => ({ ...prev, branchName: e.target.value })) } />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">Address</label>
                        <TextArea rows={ 3 } value={ form.address } onChange={ e => setForm(prev => ({ ...prev, address: e.target.value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Branch phone</label>
                        <Input value={ form.branchPhone } onChange={ e => setForm(prev => ({ ...prev, branchPhone: e.target.value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Branch email</label>
                        <Input value={ form.branchEmail } onChange={ e => setForm(prev => ({ ...prev, branchEmail: e.target.value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Time zone</label>
                        <Select className="w-full" value={ form.timeZone || undefined } onChange={ value => setForm(prev => ({ ...prev, timeZone: value })) }>
                            {buildTimeZoneOptions(form.country).map(zone => <Select.Option key={ zone } value={ zone }>{ zone }</Select.Option>)}
                        </Select>
                    </div>
                </div>
            )
        }

        if (current === 1) {
            return (
                <div className="grid gap-4 md:grid-cols-2">
                    <div>
                        <label className="block text-sm font-medium mb-1">Super admin name</label>
                        <Input value={ form.adminName } onChange={ e => setForm(prev => ({ ...prev, adminName: e.target.value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Super admin email</label>
                        <Input value={ form.adminEmail } onChange={ e => setForm(prev => ({ ...prev, adminEmail: e.target.value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Phone</label>
                        <Input value={ form.adminPhone } onChange={ e => setForm(prev => ({ ...prev, adminPhone: e.target.value })) } />
                    </div>
                    <div />
                    <div>
                        <label className="block text-sm font-medium mb-1">Password</label>
                        <Input.Password value={ form.adminPassword } onChange={ e => setForm(prev => ({ ...prev, adminPassword: e.target.value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Confirm password</label>
                        <Input.Password value={ form.adminPasswordConfirm } onChange={ e => setForm(prev => ({ ...prev, adminPasswordConfirm: e.target.value })) } />
                    </div>
                </div>
            )
        }

        if (current === 2) {
            return (
                <div className="grid gap-4 md:grid-cols-3">
                    <div>
                        <label className="block text-sm font-medium mb-1">Currency</label>
                        <Input value={ form.currency } onChange={ e => setForm(prev => ({ ...prev, currency: e.target.value.toUpperCase() })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Fiscal year start month</label>
                        <InputNumber className="w-full" min={ 1 } max={ 12 } value={ form.fiscalYearMonth } onChange={ value => setForm(prev => ({ ...prev, fiscalYearMonth: value })) } />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Fiscal year start day</label>
                        <InputNumber className="w-full" min={ 1 } max={ 31 } value={ form.fiscalYearDay } onChange={ value => setForm(prev => ({ ...prev, fiscalYearDay: value })) } />
                    </div>
                </div>
            )
        }

        if (current === 3) {
            return (
                <div className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium mb-2">Specialties</label>
                        <div className="grid gap-2 md:grid-cols-2">
                            {specialties.map(specialty => (
                                <Checkbox
                                    key={ specialty.name }
                                    checked={ form.selectedSpecialties.includes(specialty.name) }
                                    onChange={ () => toggleSpecialty(specialty.name) }
                                >
                                    { specialty.label || specialty.name }
                                </Checkbox>
                            ))}
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="block text-sm font-medium mb-1">Chargemaster template</label>
                            <Select className="w-full" value={ form.chargemasterTemplate || undefined } onChange={ value => setForm(prev => ({ ...prev, chargemasterTemplate: value })) }>
                                {templates.map(template => <Select.Option key={ template.id } value={ template.id }>{ template.name }</Select.Option>)}
                            </Select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Initial stores</label>
                            <Input value={ form.initialStores } onChange={ e => setForm(prev => ({ ...prev, initialStores: e.target.value })) } placeholder="Main Store, Pharmacy" />
                        </div>
                    </div>

                    <div className="flex items-center justify-between border rounded-lg px-4 py-3">
                        <div>
                            <div className="font-medium">Enable inpatient</div>
                            <div className="text-sm text-gray-500">Provision an initial ward and beds during setup.</div>
                        </div>
                        <Switch checked={ form.inpatientEnabled } onChange={ checked => setForm(prev => ({ ...prev, inpatientEnabled: checked })) } />
                    </div>

                    {form.inpatientEnabled && (
                        <div className="grid gap-4 md:grid-cols-3">
                            <div>
                                <label className="block text-sm font-medium mb-1">Ward name</label>
                                <Input value={ form.wardName } onChange={ e => setForm(prev => ({ ...prev, wardName: e.target.value })) } />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Bed count</label>
                                <InputNumber className="w-full" min={ 1 } value={ form.wardBeds } onChange={ value => setForm(prev => ({ ...prev, wardBeds: value })) } />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Daily rate</label>
                                <InputNumber className="w-full" min={ 0 } value={ form.wardDailyRate } onChange={ value => setForm(prev => ({ ...prev, wardDailyRate: value })) } />
                            </div>
                        </div>
                    )}
                </div>
            )
        }

        if (current === 4) {
            return (
                <div className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium mb-2">Accepted schemes</label>
                        <div className="grid gap-2 md:grid-cols-2">
                            {schemes.map(scheme => (
                                <Checkbox
                                    key={ scheme.id }
                                    checked={ form.selectedSchemes.includes(scheme.id) }
                                    onChange={ () => toggleScheme(scheme.id) }
                                >
                                    { scheme.name }
                                </Checkbox>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Custom schemes</label>
                        <Input value={ form.customSchemes } onChange={ e => setForm(prev => ({ ...prev, customSchemes: e.target.value })) } placeholder="Comma-separated custom schemes" />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <label className="block text-sm font-medium mb-1">Notification email</label>
                            <Input value={ form.notificationEmail } onChange={ e => setForm(prev => ({ ...prev, notificationEmail: e.target.value })) } />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Notification phone</label>
                            <Input value={ form.notificationPhone } onChange={ e => setForm(prev => ({ ...prev, notificationPhone: e.target.value })) } />
                        </div>
                    </div>

                    {form.country === 'Kenya' && (
                        <div className="space-y-4 border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium">Enable SHA facility integration</div>
                                    <div className="text-sm text-gray-500">Configure the facility-level SHA credentials used for eligibility, consent, and claims.</div>
                                </div>
                                <Switch checked={ form.shaEnabled } onChange={ checked => setForm(prev => ({ ...prev, shaEnabled: checked, shaBiometricEnabled: checked ? prev.shaBiometricEnabled : false })) } />
                            </div>

                            {form.shaEnabled && (
                                <>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Facility KEPH level</label>
                                            <Select value={ form.kephLevel || undefined } onChange={ value => setForm(prev => ({ ...prev, kephLevel: value })) }>
                                                {[2, 3, 4, 5, 6].map(level => <Select.Option key={ level } value={ level }>{`Level ${level}`}</Select.Option>)}
                                            </Select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">SHA base URL</label>
                                            <Input value={ form.shaBaseUrl } onChange={ e => setForm(prev => ({ ...prev, shaBaseUrl: e.target.value })) } />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">SHA client ID</label>
                                            <Input value={ form.shaClientId } onChange={ e => setForm(prev => ({ ...prev, shaClientId: e.target.value })) } />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">SHA client secret</label>
                                            <Input.Password value={ form.shaClientSecret } onChange={ e => setForm(prev => ({ ...prev, shaClientSecret: e.target.value })) } />
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between border rounded-lg px-4 py-3">
                                        <div>
                                            <div className="font-medium">Enable biometric / eKYC SHA operations</div>
                                            <div className="text-sm text-gray-500">Needed only if this facility will run biometric consent flows.</div>
                                        </div>
                                        <Switch checked={ form.shaBiometricEnabled } onChange={ checked => setForm(prev => ({ ...prev, shaBiometricEnabled: checked })) } />
                                    </div>

                                    {form.shaBiometricEnabled && (
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Facility FR code</label>
                                                <Input value={ form.shaFacilityFrCode } onChange={ e => setForm(prev => ({ ...prev, shaFacilityFrCode: e.target.value })) } />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">eKYC provider ID</label>
                                                <Input value={ form.shaEkycProviderId } onChange={ e => setForm(prev => ({ ...prev, shaEkycProviderId: e.target.value })) } />
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            )
        }

        return (
            <div className="space-y-4">
                <div className="rounded-lg border p-4 bg-gray-50">
                    <div className="font-semibold mb-2">Facility</div>
                    <div>{ form.hospitalName || '—' } · { form.country || '—' }</div>
                    <div className="text-sm text-gray-600">{ form.branchName || 'Main Branch' } · { form.timeZone || '—' }</div>
                </div>
                <div className="rounded-lg border p-4 bg-gray-50">
                    <div className="font-semibold mb-2">Clinical setup</div>
                    <div>Specialties: { form.selectedSpecialties.join(', ') || '—' }</div>
                    <div>Template: { templates.find(template => template.id === form.chargemasterTemplate)?.name || form.chargemasterTemplate || '—' }</div>
                    <div>Inpatient: { form.inpatientEnabled ? `${form.wardName} (${form.wardBeds} beds)` : 'Disabled' }</div>
                </div>
                <div className="rounded-lg border p-4 bg-gray-50">
                    <div className="font-semibold mb-2">Insurance & notifications</div>
                    <div>Schemes: {[...form.selectedSchemes, ...form.customSchemes.split(',').map(item => item.trim()).filter(Boolean)].join(', ') || '—'}</div>
                    <div>Notifications: { form.notificationEmail || '—' } { form.notificationPhone ? `· ${form.notificationPhone}` : '' }</div>
                    <div>SHA enabled: { form.country === 'Kenya' && form.shaEnabled ? 'Yes' : 'No' }</div>
                    <div>KEPH level: { form.kephLevel || '—' }</div>
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="font-semibold mb-2">After setup: go-live verification</div>
                    <p className="text-sm text-gray-700 mb-3">
                        Once the super admin signs in, walk through this quick operational smoke test before calling the facility live.
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                        {goLiveChecklist.map(item => <li key={ item }>{item}</li>)}
                    </ul>
                </div>

                {errors.length > 0 && (
                    <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4">
                        <div className="font-medium mb-2">Before finishing setup</div>
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                            {errors.map(issue => <li key={ issue }>{ issue }</li>)}
                        </ul>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-sm border p-6 md:p-8">
                <div className="mb-8">
                    <h1 className="text-2xl font-semibold mb-2">Facility setup</h1>
                    <p className="text-gray-600">Let’s configure the hospital instance before anyone logs in. This only needs to be done once.</p>
                </div>

                <Receive
                    loading={ countriesLoading || draftLoading || !restoreComplete || specialtiesLoading || templatesLoading || schemesLoading || submitLoading }
                    error={ countriesError || draftError || specialtiesError || templatesError || schemesError || submitError }
                    progress
                >
                    <Steps current={ current } size="small" className="mb-8">
                        {stepTitles.map(title => <Step key={ title } title={ title } />)}
                    </Steps>

                    <div className="min-h-[360px]">
                        {renderStep()}
                    </div>

                    <div className="mt-8 flex items-center justify-between">
                        <Button disabled={ current === 0 } onClick={ () => setCurrent(prev => prev - 1) }>
                            Back
                        </Button>

                        <div className="flex items-center gap-3">
                            {current < stepTitles.length - 1 ? (
                                <Button type="primary" onClick={ () => {
                                    const nextStep = current + 1
                                    persistDraft(nextStep)
                                    setCurrent(nextStep)
                                } }>
                                    Next
                                </Button>
                            ) : (
                                <Button type="primary" loading={ submitLoading } onClick={ performSubmit }>
                                    Finish setup
                                </Button>
                            )}
                        </div>
                    </div>
                </Receive>
            </div>
        </div>
    )
}

export default Setup
